import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import classNames from 'classnames';
import { ReactElement, ReactNode, useMemo } from 'react';

import {
  DictionaryCustomFieldType,
  ICustomDictionaryItem,
  ICustomDictionaryType,
} from '~/services/api/dictionaries/types';
import Status from '~/ui/components/common/Status';
import TruncatedText from '~/ui/components/common/TruncatedText';

import archiveIcon from '~/ui/assets/images/archiveIcon.svg';
import editGrey from '~/ui/assets/images/editGrey.svg';

import styles from '~/ui/pages/Dictionaries/Dictionaries.module.scss';

interface IProps {
  dictionaryItem: ICustomDictionaryItem;
  dictionaryType: ICustomDictionaryType;
  hasCustomFields: boolean;
  setDictionaryItem: (item: ICustomDictionaryItem | null) => void;
  onEditDictionaryItem: (item: ICustomDictionaryItem) => void;
}

const TableBodyItem = ({
  dictionaryItem,
  hasCustomFields,
  dictionaryType,
  setDictionaryItem,
  onEditDictionaryItem,
}: IProps): ReactElement => {
  const bodyCustomFields = useMemo(() => {
    if (hasCustomFields)
      return dictionaryType.customFields.map(({ name, type }) => {
        const isCheckbox = type === DictionaryCustomFieldType.CHECKBOX;
        const customFieldValue: ReactNode = dictionaryItem[name];
        return (
          <TableCell
            key={name}
            className={classNames(styles.bodyCell, {
              [styles.centeredCell]: isCheckbox,
              [styles.textCell]: !isCheckbox,
            })}
          >
            {isCheckbox ? (
              <Checkbox
                checked={Boolean(customFieldValue)}
                color="primary"
                disabled
                className={styles.rowCheckbox}
              />
            ) : (
              !!customFieldValue && <TruncatedText>{customFieldValue}</TruncatedText>
            )}
          </TableCell>
        );
      });
    return null;
  }, [dictionaryItem, dictionaryType.customFields, hasCustomFields]);

  return (
    <TableRow key={dictionaryItem.id} className={styles.tableRowHeight}>
      <TableCell className={classNames(styles.bodyCell, styles.textCell)}>
        <TruncatedText>{dictionaryItem.value}</TruncatedText>
      </TableCell>
      {hasCustomFields && bodyCustomFields}
      <TableCell className={styles.bodyCell}>
        <Status isArchived={dictionaryItem.isArchived} />
      </TableCell>
      <TableCell className={classNames(styles.bodyCell, styles.actionsCell)}>
        <div className={styles.actions}>
          {dictionaryItem.isArchived ? (
            <div
              role="presentation"
              className={styles.restoreButton}
              onClick={() => {
                setDictionaryItem(dictionaryItem);
              }}
            >
              Restore
            </div>
          ) : (
            <>
              <IconButton
                onClick={() => {
                  onEditDictionaryItem(dictionaryItem);
                }}
                className={styles.actionButton}
              >
                <img src={editGrey} alt="edit" className={styles.editIcon} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setDictionaryItem(dictionaryItem);
                }}
                className={styles.actionButton}
              >
                <img src={archiveIcon} alt="archive" className={styles.archiveIcon} />
              </IconButton>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default TableBodyItem;
